import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// actions
import {
  fetchAvailableSamplesCount,
  fetchCardSuiteDetails,
  fetchDefaultTemplates,
  fetchUserShippingAddress,
  resetSamplesFlow,
  setDesignCustomizations,
  setStepKey,
  saveAsDraft,
} from 'cards/reducers/samplesFlow/actions';
import { hideModal, openModal } from 'cards/actions/modalActions';
import { getUserContext as fetchUserContext } from 'actions/UserActions';
import { disableMultiSampleMode } from 'cards/actions/samples';

// selectors
import { getIsAdminView } from 'cards/selectors/cardCatalogSelector';
import {
  getCardSuiteDetailsSet,
  getCardType,
  getDesignCustomizationsSet,
  getShippingDetailsFetched,
  getSamplesAvailable,
  getStepKey,
  getTemplatesSet,
} from 'cards/selectors/samplesFlow';
import { getUserContext, getUserIsGuest } from 'selectors/user/userSelectors';
import { getActiveVariationOptionValues } from 'cards/reducers/pdp/selectors';

// utils
import experimentFlags, { RECAPTCHA_REFACTOR } from 'util/experimentFlags';
import {
  lookupDefaultVariation,
  lookupSelectedVariation,
} from 'cards/components/common/Samples/util/helpers';
import { DesignCustomizationsSet } from 'cards/reducers/samplesFlow/types';
import { useAppDispatch } from 'reducers/index';
import { getCartSize } from 'actions/CartActions';
import { getCartSize as getCartSizeSelector } from 'selectors/cartSelectors';
import { usePrevious } from '@zola/zola-ui/src/hooks/usePrevious';
import generateUUID from '../../../../../lib/uuid';
import { COMPONENTS, STEPS } from './constants';

import { trackEvent } from '../../../../../util/trackingHelper';
import styles from './styles.module.less';

export interface SingleSampleFlowProps {
  cardNameList?: string[];
  cardSuiteUUIDList: string[];
  cardUUIDList?: string[];
  entryPoint: string;
  flowName?: string;
  isMagnet: boolean;
  selectedColorSet: { [key: string]: string };
  reopenCurrentModal: () => void;
  setModalOptions: (newOptions: Record<string, unknown>) => void;
}

const SamplesFlow = ({
  cardNameList,
  cardSuiteUUIDList,
  cardUUIDList,
  entryPoint,
  flowName,
  isMagnet,
  selectedColorSet,
  reopenCurrentModal,
  setModalOptions,
}: SingleSampleFlowProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const activePDPVariation = useSelector(getActiveVariationOptionValues);
  const cartSize = useSelector(getCartSizeSelector);
  const cardSuiteDetailsSet = useSelector(getCardSuiteDetailsSet);
  const cardType = useSelector(getCardType);
  const designCustomizationsSet = useSelector(getDesignCustomizationsSet);
  const isAdminView = useSelector(getIsAdminView);
  const isGuest = useSelector(getUserIsGuest);
  const shippingDetailsFetched = useSelector(getShippingDetailsFetched);
  const samplesAvailable = useSelector(getSamplesAvailable);
  const stepKey = useSelector(getStepKey);
  const templatesSet = useSelector(getTemplatesSet);
  const userContext = useSelector(getUserContext);

  const [flowUUID] = useState(generateUUID());
  const previousFlowUUID = usePrevious(flowUUID);
  const [trackingParams, setTrackingParams] = useState<Record<string, unknown>>({});

  // Refresh the cart size as soon as the flow starts
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    dispatch(getCartSize());
  }, [dispatch]);

  // If the user has items in their cart or samples are unavailable, send them to the warning step
  useEffect(() => {
    if (cartSize > 0 || samplesAvailable === false) {
      dispatch(setStepKey(STEPS.WARNING));
    }
  }, [dispatch, cartSize, samplesAvailable]);

  // Fetch sample availability if unfetched
  useEffect(() => {
    if (samplesAvailable === null && cardType !== null) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      dispatch(fetchAvailableSamplesCount(cardType));
    }
  }, [dispatch, samplesAvailable, cardType]);

  // Fetch shipping details if unfetched and user is authenticated
  useEffect(() => {
    if (!shippingDetailsFetched && !isGuest) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      dispatch(fetchUserShippingAddress(userContext));
    }
  }, [dispatch, shippingDetailsFetched, isGuest, userContext]);

  // Fetch card suite details if unfetched and if opening a new instance of samples modal
  useEffect(() => {
    if (Object.keys(cardSuiteDetailsSet).length === 0 && previousFlowUUID !== flowUUID) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      dispatch(fetchCardSuiteDetails(cardSuiteUUIDList, isAdminView));
    }
  }, [dispatch, cardSuiteDetailsSet, cardSuiteUUIDList, isAdminView, previousFlowUUID, flowUUID]);

  // Once card suite details and colors are available, define the initial design customizations
  useEffect(() => {
    if (
      Object.keys(designCustomizationsSet).length === 0 &&
      selectedColorSet &&
      Object.keys(cardSuiteDetailsSet).length > 0
    ) {
      const initialCustomizationsSet: DesignCustomizationsSet = {};

      Object.entries(cardSuiteDetailsSet).forEach(([key, suiteDetails]) => {
        const defaultVariation = lookupDefaultVariation(suiteDetails, isMagnet);
        const selectedColor =
          suiteDetails.letterpress || suiteDetails.foil
            ? defaultVariation.option_values.color
            : selectedColorSet[key];

        const selected = activePDPVariation
          ? {
              ...defaultVariation.option_values,
              color: selectedColor,
              size: activePDPVariation.size,
              'reverse-printing': (activePDPVariation.size === 'postcard').toString(),
            }
          : {
              ...defaultVariation.option_values,
              color: selectedColor,
            };

        // lookup selected color variation unless letterpress or foil (then use default)
        const colorVariation = lookupSelectedVariation(selected, suiteDetails, isMagnet);

        if (colorVariation) {
          initialCustomizationsSet[key] = {
            color: colorVariation.option_values.color,
            uploadcareFile: null,
            variation: colorVariation,
          };
        }
      });

      dispatch(setDesignCustomizations(initialCustomizationsSet));
    }
  }, [
    dispatch,
    selectedColorSet,
    cardSuiteDetailsSet,
    designCustomizationsSet,
    activePDPVariation,
    isMagnet,
  ]);

  // Once card suite details and default customizations are available, fetch the corresponding templates
  useEffect(() => {
    if (
      Object.keys(templatesSet).length === 0 &&
      Object.keys(cardSuiteDetailsSet).length > 0 &&
      Object.keys(designCustomizationsSet).length > 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      dispatch(
        fetchDefaultTemplates(cardSuiteUUIDList, cardSuiteDetailsSet, {}, designCustomizationsSet)
      );
    }
  }, [dispatch, cardSuiteDetailsSet, templatesSet, cardSuiteUUIDList, designCustomizationsSet]);

  // Send "Flow Started" event when loading is complete
  useEffect(() => {
    if (Object.keys(cardSuiteDetailsSet).length === 0) {
      return;
    }

    const params = {
      entry_point: entryPoint,
      flow_run_id: flowUUID,
      flow_name: flowName,
      business_unit: 'INVITATIONS',
      product_id: cardSuiteUUIDList,
      sku_id: cardUUIDList,
      category: cardType,
      name: cardNameList,
      brand: Object.values(cardSuiteDetailsSet).map(details => details.name),
    };

    setTrackingParams(params);
    trackEvent('Flow Started', params);
  }, [
    entryPoint,
    flowName,
    cardSuiteUUIDList,
    cardUUIDList,
    cardType,
    cardNameList,
    cardSuiteDetailsSet,
    flowUUID,
  ]);

  // Send "Flow Step Viewed" event every time the user changes steps.
  useEffect(() => {
    if (stepKey && Object.keys(trackingParams).length > 0) {
      trackEvent('Flow Step Viewed', {
        ...trackingParams,
        step_name: stepKey,
      });
    }
  }, [stepKey, trackingParams]);

  const exitFlow = useCallback(() => {
    return dispatch(disableMultiSampleMode())
      .then(() => dispatch(resetSamplesFlow()))
      .then(() => dispatch(hideModal()));
  }, [dispatch]);

  // For existing non-invitations accounts, create an invitation account and save sample suites as drafts
  const exitFlowAndSaveDraft = useCallback(() => {
    const willSaveDraft = !userContext.is_guest && !userContext.has_invitation_account;
    const saveDraftRequest = willSaveDraft
      ? dispatch(saveAsDraft(cardSuiteUUIDList, userContext))
      : Promise.resolve(false);

    let draftCreated = false;

    return saveDraftRequest
      .then(saveDraftSucceeded => {
        draftCreated = saveDraftSucceeded;
      })
      .then(() => dispatch(disableMultiSampleMode()))
      .then(() => dispatch(resetSamplesFlow()))
      .then(() => dispatch(hideModal()))
      .then(() => {
        if (draftCreated) {
          dispatch(openModal('CLOSE_CUSTOM', { projectType: cardType }));
        }
      });
  }, [dispatch, userContext, cardSuiteUUIDList, cardType]);

  // Use exitFlowAndSaveDraft even if the user directly Xs out of the modal
  useEffect(() => {
    // If a draft will be saved on exit, don't warn the user about losing changes
    const willSaveDraft = !userContext.is_guest && !userContext.has_invitation_account;
    setModalOptions({ closeCallback: exitFlowAndSaveDraft, warnOnClose: !willSaveDraft });
  }, [exitFlowAndSaveDraft, setModalOptions, userContext]);

  // Opens the login/signup modal, and sends users back to the samples flow on exit
  const handleLogin = useCallback(() => {
    const isRecaptchaRefactor = experimentFlags.getFlag(RECAPTCHA_REFACTOR, userContext);
    const loginCloseCallback = () => {
      if (cardType === null) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      dispatch(fetchUserContext());
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      dispatch(fetchAvailableSamplesCount(cardType));
      reopenCurrentModal();
    };

    dispatch(hideModal());
    if (isRecaptchaRefactor) {
      setTimeout(() => {
        window.zolaNav.openLoginModal({
          loginCallback: loginCloseCallback,
          closeCallback: loginCloseCallback,
        });
      });
    } else {
      window.location.assign(`/account/login?returnTo=${window.location.pathname}`);
    }
  }, [cardType, dispatch, reopenCurrentModal, userContext]);

  // Opens the paper type comparison modal
  const handleShowPaperTypeModal = useCallback(() => {
    dispatch(
      openModal(
        'PAPER_TYPE',
        {},
        {
          closeCallback: reopenCurrentModal,
          hideModalAfterCallback: false,
          style: { marginTop: 0 },
        }
      )
    );
  }, [dispatch, reopenCurrentModal]);

  // Don't show anything until card suite details are available
  if (
    !cardSuiteDetailsSet ||
    Object.keys(cardSuiteDetailsSet).length === 0 ||
    samplesAvailable === null
  ) {
    return null;
  }

  const StepComponent = COMPONENTS[stepKey];

  return (
    <div className={styles.sampleFlowWrapper}>
      <StepComponent
        cardSuiteUUIDList={cardSuiteUUIDList}
        exitFlow={exitFlow}
        exitFlowAndSaveDraft={exitFlowAndSaveDraft}
        isAdminView={isAdminView}
        isGuest={isGuest}
        isMagnet={isMagnet}
        login={handleLogin}
        showPaperTypeModal={handleShowPaperTypeModal}
        samplesAvailable={samplesAvailable}
        trackingParams={trackingParams}
      />
    </div>
  );
};

export default SamplesFlow;
